import { z } from 'zod';

/**
 * SSE topics. One per domain area; the web subscribes to the set it needs and
 * `useLiveQuery` maps a topic to TanStack Query invalidations. Keep the list
 * closed so both ends agree — module agents add their topic here.
 */
export const EVENT_TOPICS = [
  'articles',
  'barcodes',
  'suppliers',
  'restaurants',
  'warehouse',
  'stock',
  'pallets',
  'lots',
  'stocktakes',
  'purchase-orders',
  'receipts',
  'sales-orders',
  'delivery-schedules',
  'pick-rounds',
  'pick-tasks',
  'shipments',
  'receptions',
  'production',
  'claims',
  'notifications',
  'devices',
  'printers',
  'print-jobs',
  'erp',
  'sync-rejections',
  'settings',
  'users',
] as const;

export const eventTopicSchema = z.enum(EVENT_TOPICS);
export type EventTopic = z.infer<typeof eventTopicSchema>;

/**
 * One row of `event_log`, as delivered over `GET /api/events/stream`.
 * `id` is the bigserial rendered as a string — it doubles as the SSE
 * `Last-Event-ID` replay cursor.
 */
export const sseEventSchema = z.object({
  id: z.string(),
  topic: eventTopicSchema,
  entity: z.string(),
  entityId: z.string().nullable(),
  payload: z.unknown(),
  at: z.iso.datetime(),
});
export type SseEvent<P = unknown> = Omit<z.infer<typeof sseEventSchema>, 'payload'> & { payload: P };

/** GET /api/events/stream?topics=stock,sales-orders */
export const eventStreamQuerySchema = z.object({
  topics: z
    .string()
    .optional()
    .transform((v) =>
      v
        ? v
            .split(',')
            .map((t) => t.trim())
            .filter(Boolean)
        : undefined,
    )
    .pipe(z.array(eventTopicSchema).min(1).optional()),
  /** Fallback for clients that cannot set the `Last-Event-ID` header. */
  lastEventId: z.string().regex(/^\d+$/).optional(),
});
export type EventStreamQuery = z.infer<typeof eventStreamQuerySchema>;

/** Payload of the postgres `NOTIFY wms_events` wake-up (not the event itself). */
export const eventNotifySchema = z.object({
  id: z.string(),
  topic: z.string(),
});
export type EventNotify = z.infer<typeof eventNotifySchema>;

export const EVENT_NOTIFY_CHANNEL = 'wms_events';
